// oxlint-disable react/only-export-components
import {
  faAnglesUpDown,
  faCheck,
  faFilter,
  faMagnifyingGlass,
  faMinus,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Command as CommandPrimitive } from "cmdk"
import { Popover as PopoverPrimitive } from "radix-ui"
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import type {
  ComponentProps,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from "react"
import { cn } from "cn"

import { useIsMobile } from "../../../hooks/use-mobile"
import { Button } from "../button/button"
import { DrawerClose, DrawerFooter, DrawerTitle } from "../drawer/drawer"
import { EmptyState } from "../empty-state/empty-state"
import type { EmptyStateProps } from "../empty-state/empty-state"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"

/**
 * Figma trennt „Combobox Popup" (Einfachauswahl) und „Combobox Multiple"
 * (Mehrfachauswahl) in zwei Component-Sets, die sich Liste, Item und Trigger
 * teilen — hier daher **eine** Komponente, `multiple` schaltet um.
 *
 * Label/Helfertext/Invalid des Figma-Sets sind bewusst keine Props: bare
 * Komponente + `Field`-Kit, `aria-invalid` direkt auf `ComboboxTrigger`
 * (siehe `select/select.tsx`).
 *
 * Technische Basis ist shadcns Combobox-Muster (Popover + cmdk `Command`).
 * Mobile (Drawer statt Popover) übernimmt der V2-`Popover`.
 */

type ComboboxOption = {
  value: string
  label: string
  disabled?: boolean
  /**
   * Figma „label + dot": CSS-Farbwert des Punkts (z. B. `#9932dd`). Bewusst
   * ein Farbwert statt Tailwind-Klasse, da die Farbe zur Laufzeit aus den
   * Daten kommt und Tailwind dynamische Klassen nicht erzeugen kann.
   */
  color?: string
  /** Figma „label + avatar": z. B. ein v2 `Avatar` (type="initials", size="xs"). */
  avatar?: ReactNode
}

type ComboboxContextValue = {
  options: ComboboxOption[]
  multiple: boolean
  selected: string[]
  toggle: (value: string) => void
  setSelected: (values: string[]) => void
  clear: () => void
  open: boolean
  setOpen: (open: boolean) => void
  disabled: boolean
}

const ComboboxContext = createContext<ComboboxContextValue | null>(null)

function useCombobox() {
  const context = useContext(ComboboxContext)
  if (!context) {
    throw new Error("Combobox-Bausteine müssen innerhalb von <Combobox> liegen")
  }
  return context
}

type ComboboxBaseProps = {
  options: ComboboxOption[]
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  /** Wie `Popover` `modal` — nötig, wenn die Combobox in einem anderen modalen Layer (Sheet, modaler Popover) liegt, sonst blockiert dessen Scroll-Lock das Mausrad in der Liste. */
  modal?: boolean
  children?: ReactNode
}

type ComboboxSingleProps = ComboboxBaseProps & {
  multiple?: false
  value?: string | null
  defaultValue?: string | null
  onValueChange?: (value: string | null) => void
}

type ComboboxMultipleProps = ComboboxBaseProps & {
  multiple: true
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
}

type ComboboxProps = ComboboxSingleProps | ComboboxMultipleProps

function useControllable<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (value: T) => void
) {
  const [internal, setInternal] = useState(defaultValue)
  const isControlled = value !== undefined
  const current = isControlled ? value : internal
  function set(next: T) {
    if (!isControlled) setInternal(next)
    onChange?.(next)
  }
  return [current, set] as const
}

function Combobox(props: ComboboxProps) {
  const {
    options,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    disabled = false,
    modal = false,
    children,
  } = props
  const multiple = props.multiple === true

  const [open, setOpen] = useControllable(openProp, defaultOpen, onOpenChange)

  // Einfach- und Mehrfachauswahl intern einheitlich als Array geführt.
  const [selected, setSelectedState] = useControllable<string[]>(
    props.value === undefined
      ? undefined
      : props.value === null
        ? []
        : Array.isArray(props.value)
          ? props.value
          : [props.value],
    props.defaultValue == null
      ? []
      : Array.isArray(props.defaultValue)
        ? props.defaultValue
        : [props.defaultValue]
  )

  function setSelected(values: string[]) {
    setSelectedState(values)
    // Kein Narrowing über `props.multiple`: Apps mit `strictNullChecks: false`
    // typisieren das Discriminated Union sonst anders (`string & string[]`).
    const onValueChange = props.onValueChange as
      ((value: string[] | string | null) => void) | undefined
    onValueChange?.(multiple ? values : (values[0] ?? null))
  }

  function toggle(value: string) {
    if (multiple) {
      setSelected(
        selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value] // Reihenfolge = Auswahlreihenfolge (Figma)
      )
    } else {
      // Einfachauswahl: erneuter Klick auf das Item behält den Wert
      setSelected([value])
      setOpen(false)
    }
  }

  const context: ComboboxContextValue = {
    options,
    multiple,
    selected,
    toggle,
    setSelected,
    clear: () => setSelected([]),
    open,
    setOpen,
    disabled,
  }

  return (
    <ComboboxContext.Provider value={context}>
      <Popover modal={modal} open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </ComboboxContext.Provider>
  )
}

/** Farbpunkt einer Option; Farbe per `style`, weil sie erst zur Laufzeit feststeht. */
function ComboboxDot({
  color,
  className,
}: {
  color: string
  className?: string
}) {
  return (
    <span
      aria-hidden
      className={cn("size-2.5 shrink-0 rounded-full", className)}
      style={{ backgroundColor: color }}
    />
  )
}

/** Figma „ComboboxChip": Farbpunkt, Label, Entfernen-Button. */
type ComboboxChipProps = ComponentProps<"span"> & {
  option: ComboboxOption
  onRemove?: () => void
}

function ComboboxChip({
  option,
  onRemove,
  className,
  ...props
}: ComboboxChipProps) {
  return (
    <span
      data-slot="combobox-chip"
      className={cn(
        "inline-flex h-5 max-w-32 min-w-5 shrink-0 items-center justify-center gap-1 rounded-full bg-neutrals-v2-200 px-1.5 py-0.5 text-xs leading-4 text-brand-blue-100",
        className
      )}
      {...props}
    >
      {option.color && <ComboboxDot color={option.color} className="size-2" />}
      <span className="min-w-0 flex-1 truncate text-center">
        {option.label}
      </span>
      {onRemove && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`${option.label} entfernen`}
          className="flex size-3.5 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none"
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
          }}
        >
          <FontAwesomeIcon icon={faXmark} className="size-3" />
        </button>
      )}
    </span>
  )
}

/** Breite der „+n"-Chip-Reserve beim einzeiligen Verhalten (Figma „Höhe begrenzt"). */
const OVERFLOW_CHIP_WIDTH = 34
const CHIP_GAP = 4

/**
 * Figma „Verhalten (Höhe begrenzt)": eine Zeile, überzählige Chips werden zu
 * „+n". Passende Chips werden über eine unsichtbare Messzeile bestimmt.
 */
function ComboboxChipsSingleRow({ chips }: { chips: ComboboxOption[] }) {
  const { toggle } = useCombobox()
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(chips.length)
  // `chips` ist pro Render ein neues Array — stabiler Schlüssel, damit der
  // ResizeObserver nicht bei jedem Render neu aufgebaut wird.
  const chipsKey = chips.map((chip) => `${chip.value}:${chip.label}`).join("|")

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    function compute() {
      if (!container || !measure) return
      const widths = Array.from(measure.children).map(
        (child) => (child as HTMLElement).offsetWidth
      )
      const available = container.clientWidth
      const total =
        widths.reduce((sum, width) => sum + width, 0) +
        CHIP_GAP * Math.max(widths.length - 1, 0)
      if (total <= available) {
        setVisibleCount(widths.length)
        return
      }
      let used = 0
      let count = 0
      for (const width of widths) {
        const next = used + (count > 0 ? CHIP_GAP : 0) + width
        if (next + CHIP_GAP + OVERFLOW_CHIP_WIDTH > available) break
        used = next
        count += 1
      }
      setVisibleCount(count)
    }

    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(container)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chipsKey bildet chips ab
  }, [chipsKey])

  const hidden = chips.length - visibleCount

  return (
    <div
      ref={containerRef}
      data-slot="combobox-chips"
      className="relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
    >
      {chips.slice(0, visibleCount).map((chip) => (
        <ComboboxChip
          key={chip.value}
          option={chip}
          onRemove={() => toggle(chip.value)}
        />
      ))}
      {hidden > 0 && (
        <span
          data-slot="combobox-chip-overflow"
          className="inline-flex h-5 shrink-0 items-center rounded-full bg-neutrals-v2-200 px-1.5 py-0.5 text-xs leading-4 text-labels-primary"
        >
          +{hidden}
        </span>
      )}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 left-0 flex gap-1 whitespace-nowrap"
      >
        {chips.map((chip) => (
          <ComboboxChip key={chip.value} option={chip} onRemove={() => {}} />
        ))}
      </div>
    </div>
  )
}

type ComboboxTriggerProps = Omit<ComponentProps<"div">, "children"> & {
  placeholder?: string
  /**
   * Nur bei `multiple`. Figma „Verhalten (Höhe unbegrenzt)": Chips umbrechen
   * und das Feld wächst in die Höhe. Standard ist die einzeilige Anzeige mit
   * „+n" (Höhe begrenzt).
   */
  wrap?: boolean
}

/**
 * Das Feld (Figma „field"). `div` statt `button`, weil die Chips und der
 * Zurücksetzen-Button selbst interaktiv sind — Rolle/Tastatur daher manuell.
 * Für die Label-Zuordnung `aria-labelledby` setzen (ein `<label for>` greift
 * auf einem `div` nicht).
 */
function ComboboxTrigger({
  className,
  placeholder = "Auswählen",
  wrap = false,
  onKeyDown,
  ...props
}: ComboboxTriggerProps) {
  const {
    options,
    multiple,
    selected,
    toggle,
    clear,
    open,
    setOpen,
    disabled,
  } = useCombobox()

  const selectedOptions = selected
    .map((value) => options.find((option) => option.value === value))
    .filter((option): option is ComboboxOption => option !== undefined)

  const hasValue = selectedOptions.length > 0
  const showChips = multiple && hasValue
  const grows = showChips && wrap

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event)
    if (event.defaultPrevented || disabled) return
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault()
      setOpen(true)
    }
  }

  return (
    <PopoverTrigger asChild>
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        data-slot="combobox-trigger"
        data-state={open ? "open" : "closed"}
        data-disabled={disabled || undefined}
        className={cn(
          "flex min-h-9 w-full cursor-pointer items-center gap-1 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary pr-3 pl-4 text-sm leading-5 text-labels-primary transition-colors outline-none focus-visible:border-brand-blue-100 aria-disabled:cursor-not-allowed aria-disabled:text-labels-secondary aria-invalid:border-error-red-200 data-[state=open]:border-brand-blue-100",
          showChips && "pl-2",
          grows && "items-start rounded-xl py-2",
          className
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {!hasValue && (
          <span
            data-slot="combobox-placeholder"
            className="min-w-0 flex-1 truncate text-labels-secondary"
          >
            {placeholder}
          </span>
        )}

        {hasValue && !multiple && (
          <span
            data-slot="combobox-value"
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            {selectedOptions[0].color && (
              <ComboboxDot
                color={selectedOptions[0].color}
                className={cn(disabled && "opacity-50")}
              />
            )}
            <span className="min-w-0 flex-1 truncate">
              {selectedOptions[0].label}
            </span>
          </span>
        )}

        {showChips &&
          (wrap ? (
            <div
              data-slot="combobox-chips"
              className="flex min-w-0 flex-1 flex-wrap items-center gap-1"
            >
              {selectedOptions.map((option) => (
                <ComboboxChip
                  key={option.value}
                  option={option}
                  onRemove={() => toggle(option.value)}
                />
              ))}
            </div>
          ) : (
            <ComboboxChipsSingleRow chips={selectedOptions} />
          ))}

        <span
          data-slot="combobox-trigger-actions"
          className={cn("flex shrink-0 items-center gap-1", grows && "h-5")}
        >
          {multiple && hasValue && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Auswahl entfernen"
              data-slot="combobox-clear"
              className="flex size-5 cursor-pointer items-center justify-center rounded-full bg-neutrals-v2-200 outline-none"
              onClick={(event: MouseEvent) => {
                event.stopPropagation()
                clear()
              }}
            >
              <FontAwesomeIcon icon={faXmark} className="size-3.5" />
            </button>
          )}
          <FontAwesomeIcon icon={faAnglesUpDown} className="size-4.5" />
        </span>
      </div>
    </PopoverTrigger>
  )
}

type ComboboxContentProps = ComponentProps<typeof PopoverContent> & {
  /** Nur Mobile (Drawer): Titel im Header, Figma „Title". */
  title?: string
  /** Nur Mobile + `multiple`: Beschriftung des Abschluss-Buttons (Figma „CTAs"). */
  doneLabel?: string
}

/** Liste (Figma „Combobox List"): Popover auf Desktop, Drawer auf Mobile. */
function ComboboxContent({
  className,
  children,
  title = "Auswählen",
  doneLabel = "Fertig",
  ...props
}: ComboboxContentProps) {
  const { multiple } = useCombobox()
  const isMobile = useIsMobile()

  return (
    <PopoverContent
      align="start"
      sideOffset={4}
      className={cn(
        // Mobile (Figma „Drawer"): Grabber 12px unter der Oberkante, 16px Abstand zum Titel
        "gap-0 max-md:gap-4 max-md:pt-3 md:w-(--radix-popover-trigger-width) md:min-w-33",
        className
      )}
      {...props}
    >
      {isMobile && <DrawerTitle>{title}</DrawerTitle>}
      <CommandPrimitive
        data-slot="combobox-command"
        className="flex min-h-0 w-full flex-col"
      >
        {children}
      </CommandPrimitive>
      {isMobile && multiple && (
        <DrawerFooter className="px-4">
          <DrawerClose asChild>
            <Button>{doneLabel}</Button>
          </DrawerClose>
        </DrawerFooter>
      )}
    </PopoverContent>
  )
}

type ComboboxSearchProps = ComponentProps<typeof CommandPrimitive.Input> & {
  /** Figma „Filter Button" neben dem Suchfeld („Zusätzlicher Filter"), z. B. ein icon-only `Button`. */
  filter?: ReactNode
}

/** Figma „Search Input" in der Liste. */
function ComboboxSearch({
  className,
  filter,
  placeholder = "Suchen",
  ...props
}: ComboboxSearchProps) {
  return (
    <div
      data-slot="combobox-search"
      className="flex shrink-0 items-start gap-2 bg-backgrounds-primary px-4 pt-4 pb-1 max-md:pt-0 max-md:pb-2"
    >
      <div className="flex h-9 min-w-0 flex-1 items-center gap-1 rounded-full border-2 border-neutrals-v2-300 px-4 focus-within:border-brand-blue-100">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="size-5.5 shrink-0 text-brand-red-200"
        />
        <CommandPrimitive.Input
          placeholder={placeholder}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-sm leading-5 text-labels-primary outline-none placeholder:text-labels-secondary",
            className
          )}
          {...props}
        />
      </div>
      {filter}
    </div>
  )
}

function ComboboxList({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="combobox-list"
      className={cn(
        "max-h-72 overflow-x-hidden overflow-y-auto pb-1 max-md:mx-4 md:max-h-[min(20rem,var(--radix-popover-content-available-height))]",
        // Figma: 1px-Trenner zwischen den Einträgen
        "[&_[cmdk-list-sizer]]:flex [&_[cmdk-list-sizer]]:flex-col [&_[cmdk-list-sizer]>*+*]:border-t [&_[cmdk-list-sizer]>*+*]:border-separators-non-opaque",
        className
      )}
      {...props}
    />
  )
}

type ComboboxCheckState = "unchecked" | "checked" | "indeterminate"

/** Figma „Check Element": Checkbox-Optik bei `multiple`, nur Haken bei Einfachauswahl. */
function ComboboxCheck({
  state,
  multiple,
}: {
  state: ComboboxCheckState
  multiple: boolean
}) {
  if (!multiple) {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center text-brand-red-200">
        {state === "checked" && (
          <FontAwesomeIcon icon={faCheck} className="size-4.5" />
        )}
      </span>
    )
  }
  return (
    <span
      data-slot="combobox-check"
      data-state={state}
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-md border-2 border-neutrals-v2-400 bg-backgrounds-primary text-neutrals-v2-100",
        state !== "unchecked" && "border-0 bg-brand-red-200"
      )}
    >
      {state === "checked" && (
        <FontAwesomeIcon icon={faCheck} className="size-3.5" />
      )}
      {state === "indeterminate" && (
        <FontAwesomeIcon icon={faMinus} className="size-3.5" />
      )}
    </span>
  )
}

// Figma: Hover-Hintergrund und Trennlinien sind eckig (nur der Focus-Zustand hat Radius 8,
// der hier wie bei `SelectItem` auf den Hover-Stil vereinheitlicht ist) — `border-t` der
// Trenner folgt dem Radius, daher `rounded-none`.
const itemClassName =
  "flex w-full cursor-pointer items-center rounded-none px-4 py-1.5 text-sm leading-5 text-labels-primary outline-none select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:text-labels-tertiary data-[selected=true]:bg-neutrals-v2-200"

type ComboboxItemProps = Omit<
  ComponentProps<typeof CommandPrimitive.Item>,
  "value" | "children" | "onSelect"
> & {
  option: ComboboxOption
}

/**
 * Figma „Combobox Item" (Varianten label / label + dot / label + avatar —
 * ergeben sich aus `option.color`/`option.avatar`).
 *
 * Figma unterscheidet „hover" (Hintergrund) und „focus" (Rahmen). cmdk meldet
 * Zeiger und Tastatur einheitlich über `data-selected` — hier auf den
 * Hintergrund-Stil vereinheitlicht (wie bei `SelectItem`).
 */
function ComboboxItem({ option, className, ...props }: ComboboxItemProps) {
  const { multiple, selected, toggle } = useCombobox()
  const isSelected = selected.includes(option.value)

  return (
    <CommandPrimitive.Item
      data-slot="combobox-item"
      value={option.value}
      keywords={[option.label]}
      disabled={option.disabled}
      onSelect={() => toggle(option.value)}
      className={cn(itemClassName, option.avatar && "py-2", className)}
      {...props}
    >
      <span className="flex h-7 min-w-0 flex-1 items-center gap-2">
        {option.avatar}
        {option.color && <ComboboxDot color={option.color} />}
        <span className="min-w-0 flex-1 truncate">{option.label}</span>
        <ComboboxCheck
          multiple={multiple}
          state={isSelected ? "checked" : "unchecked"}
        />
      </span>
    </CommandPrimitive.Item>
  )
}

/** „Alle auswählen (n/m)": Zähler und Check-Zustand über die auswählbaren Optionen. */
function getSelectAllState(options: ComboboxOption[], selected: string[]) {
  const selectable = options.filter((option) => !option.disabled)
  const count = selectable.filter((option) =>
    selected.includes(option.value)
  ).length
  const state: ComboboxCheckState =
    count === 0
      ? "unchecked"
      : count === selectable.length
        ? "checked"
        : "indeterminate"
  return { selectable, count, state }
}

/** Neuer Wert nach „Alle auswählen": alle abwählen, wenn alle gewählt sind, sonst ergänzen. */
function toggleAll(
  selectable: ComboboxOption[],
  selected: string[],
  state: ComboboxCheckState
) {
  const values = selectable.map((option) => option.value)
  return state === "checked"
    ? selected.filter((value) => !values.includes(value))
    : [...selected, ...values.filter((value) => !selected.includes(value))]
}

type ComboboxSelectAllProps = Omit<
  ComponentProps<typeof CommandPrimitive.Item>,
  "onSelect"
> & {
  /**
   * Auf diese Optionen beschränken, z. B. die nach einem `ComboboxFilter`
   * sichtbaren. Standard: alle `options` der `Combobox`.
   */
  options?: ComboboxOption[]
}

/** Figma-Variante „select all button": „Alle auswählen (n/m)" — nur bei `multiple`. */
function ComboboxSelectAll({
  className,
  children,
  options: scope,
  ...props
}: ComboboxSelectAllProps) {
  const { options, multiple, selected, setSelected } = useCombobox()
  if (!multiple) return null

  const { selectable, count, state } = getSelectAllState(
    scope ?? options,
    selected
  )

  return (
    <CommandPrimitive.Item
      data-slot="combobox-select-all"
      forceMount
      value="__select-all__"
      onSelect={() => setSelected(toggleAll(selectable, selected, state))}
      className={cn(itemClassName, "font-semibold", className)}
      {...props}
    >
      <span className="flex h-7 min-w-0 flex-1 items-center gap-2">
        <span className="min-w-0 flex-1 truncate">
          {children ?? `Alle auswählen (${count}/${selectable.length})`}
        </span>
        <ComboboxCheck multiple state={state} />
      </span>
    </CommandPrimitive.Item>
  )
}

/** Figma-Variante „reset button": „Auswahl entfernen". */
function ComboboxClearItem({
  className,
  children = "Auswahl entfernen",
  ...props
}: Omit<ComponentProps<typeof CommandPrimitive.Item>, "onSelect">) {
  const { clear, multiple, setOpen } = useCombobox()

  return (
    <CommandPrimitive.Item
      data-slot="combobox-clear-item"
      forceMount
      value="__clear__"
      onSelect={() => {
        clear()
        if (!multiple) setOpen(false)
      }}
      className={cn(itemClassName, "h-10 font-semibold", className)}
      {...props}
    >
      {children}
    </CommandPrimitive.Item>
  )
}

/**
 * Figma „Keine Ergebnisse" — nutzt den V2-`EmptyState` ohne Icon. `title`
 * statt `children`, weil `EmptyState` hier einen String erwartet; weitere
 * `EmptyState`-Props (`description`, `action`, …) werden durchgereicht.
 */
function ComboboxEmpty({
  title = "Keine Ergebnisse",
  ...props
}: Omit<EmptyStateProps, "icon" | "title"> & { title?: string }) {
  return (
    <CommandPrimitive.Empty data-slot="combobox-empty" className="px-4 py-3">
      <EmptyState icon={null} title={title} {...props} />
    </CommandPrimitive.Empty>
  )
}

type ComboboxFilterProps = {
  /** Auswählbare Filterwerte (Figma „Zusätzlicher Filter"), z. B. Vertragsarten. */
  options: ComboboxOption[]
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
  /** Zugänglicher Name des Buttons. */
  "aria-label"?: string
  className?: string
}

/**
 * Figma „Filter Button" + zweite Liste („Zusätzlicher Filter"): gehört in den
 * `filter`-Slot von `ComboboxSearch`. Zeigt die Anzahl aktiver Filter als
 * Badge. Die Komponente verwaltet nur die Filterauswahl — das Filtern der
 * `ComboboxItem`s selbst übernimmt der Konsument anhand von `value`.
 *
 * Immer ein Popover (auch auf Mobile): Figma sieht dort keinen Filter vor,
 * ein verschachtelter Drawer wäre unverhältnismäßig.
 */
function ComboboxFilter({
  options,
  value,
  defaultValue = [],
  onValueChange,
  className,
  ...props
}: ComboboxFilterProps) {
  const [selected, setSelected] = useControllable(
    value,
    defaultValue,
    onValueChange
  )
  const { selectable, count, state } = getSelectAllState(options, selected)

  function toggle(optionValue: string) {
    setSelected(
      selected.includes(optionValue)
        ? selected.filter((item) => item !== optionValue)
        : [...selected, optionValue]
    )
  }

  // Pfeiltasten wandern durch die Zeilen (Buttons sind sonst nur per Tab erreichbar).
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
    const rows = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>("[role=option]")
    )
    const index = rows.indexOf(document.activeElement as HTMLElement)
    const next = event.key === "ArrowDown" ? index + 1 : index - 1
    rows[(next + rows.length) % rows.length]?.focus()
    event.preventDefault()
  }

  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          data-slot="combobox-filter"
          aria-label={props["aria-label"] ?? "Filter"}
          className={cn(
            "flex h-9 min-w-9 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full text-labels-primary outline-none focus-visible:border-2 focus-visible:border-brand-blue-100",
            count > 0 && "px-4",
            className
          )}
        >
          <FontAwesomeIcon icon={faFilter} className="size-5.5" />
          {selected.length > 0 && (
            <span
              data-slot="combobox-filter-count"
              className="flex min-w-6 items-center justify-center rounded-full bg-neutrals-v2-200 px-2 py-1 text-xs leading-4 font-semibold"
            >
              {selected.length}
            </span>
          )}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-slot="combobox-filter-content"
          align="end"
          sideOffset={4}
          className="z-50 w-54 overflow-hidden rounded-xl border border-neutrals-v2-200 bg-backgrounds-primary pb-1 shadow-popover outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <div
            role="listbox"
            aria-multiselectable
            aria-label={props["aria-label"] ?? "Filter"}
            className="flex max-h-72 flex-col overflow-y-auto [&>*+*]:border-t [&>*+*]:border-separators-non-opaque"
            onKeyDown={handleKeyDown}
          >
            <FilterRow
              label={`Alle auswählen (${count}/${selectable.length})`}
              state={state}
              className="font-semibold"
              onClick={() =>
                setSelected(toggleAll(selectable, selected, state))
              }
            />
            {options.map((option) => (
              <FilterRow
                key={option.value}
                label={option.label}
                state={
                  selected.includes(option.value) ? "checked" : "unchecked"
                }
                disabled={option.disabled}
                onClick={() => toggle(option.value)}
              />
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

function FilterRow({
  label,
  state,
  className,
  ...props
}: Omit<ComponentProps<"button">, "children"> & {
  label: string
  state: ComboboxCheckState
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={state === "checked"}
      className={cn(
        itemClassName,
        "text-left hover:bg-neutrals-v2-200 focus-visible:bg-neutrals-v2-200 disabled:pointer-events-none disabled:text-labels-tertiary",
        className
      )}
      {...props}
    >
      <span className="flex h-7 min-w-0 flex-1 items-center gap-2">
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ComboboxCheck multiple state={state} />
      </span>
    </button>
  )
}

export {
  Combobox,
  ComboboxChip,
  ComboboxClearItem,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxFilter,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxSelectAll,
  ComboboxTrigger,
}
export type {
  ComboboxContentProps,
  ComboboxFilterProps,
  ComboboxItemProps,
  ComboboxOption,
  ComboboxProps,
  ComboboxSearchProps,
  ComboboxSelectAllProps,
  ComboboxTriggerProps,
}
